import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import 'dayjs/locale/es'
import 'dayjs/locale/ar'
import en from './lang/en.json'
import fr from './lang/fr.json'
import es from './lang/es.json'
import ar from './lang/ar.json'
import {
    DEFAULT_LOCALE,
    getLocaleDefinition,
    LOCALE_CODES,
} from '@/configs/locale.config'
import { useLocaleStore } from '@/store/localeStore'

/**
 * One flat resource bundle per language, imported rather than fetched.
 *
 * The template's default is `i18next-http-backend`, which splits each language
 * into a file the browser requests on switch. We bundle instead: the whole app
 * is behind a login on a single origin, the dictionaries are small next to the
 * JS that renders them, and a fetched dictionary means the first paint after a
 * language change renders raw key names. Bundling costs a few KB and removes a
 * loading state that would otherwise need handling in every view.
 */
const resources = {
    en: { translation: en },
    fr: { translation: fr },
    es: { translation: es },
    ar: { translation: ar },
}

i18n.use(initReactI18next).init({
    resources,
    lng: useLocaleStore.getState().locale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: LOCALE_CODES,
    // The dictionaries are flat: one entry per dotted key, rather than nested
    // objects. i18next would otherwise read every dot as a level and fail to
    // find `meetings.state.queued` in a flat file. Flat also means a key can
    // never collide with a namespace — adding `rules.delete` next to an
    // existing `rules.delete.*` would be an error in a nested file and is
    // simply another line here — and it keeps a translation diff readable.
    keySeparator: false,
    // No namespaces either, so a key containing ':' could not be misread as
    // one. None do today; this stops that from becoming a trap later.
    nsSeparator: false,
    interpolation: {
        // React escapes for us; letting i18next escape as well double-encodes
        // any apostrophe in an interpolated client name.
        escapeValue: false,
    },
    returnNull: false,
})

/**
 * Apply a language to everything outside React that has to know about it.
 *
 * Three things, and all three matter:
 *  - `lang` on <html>, so screen readers switch voice and the browser picks
 *    the right hyphenation and font fallbacks.
 *  - `dir` on <html>, which is the *only* thing driving RTL. Tailwind's `ltr:`
 *    and `rtl:` variants compile to `[dir="ltr"] &` / `[dir="rtl"] &`, and the
 *    vendored `components/ui` kit reads direction from ConfigProvider — so
 *    setting this attribute plus the context value flips the entire interface.
 *  - dayjs's global locale, since every date in the app is formatted through it.
 */
export const applyLocaleToDocument = (code: string) => {
    const definition = getLocaleDefinition(code)

    dayjs.locale(definition.dayjsLocale)

    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', definition.tag)
        document.documentElement.setAttribute('dir', definition.direction)
    }

    return definition
}

// The store is rehydrated from localStorage before React mounts, so the first
// paint is already in the right language and direction — no flash of English.
applyLocaleToDocument(useLocaleStore.getState().locale)

export default i18n
